'use client';

import * as React from 'react';

import { type AnalysisStatus, isAnalysisInFlight } from '@/types/entity-verification';
import { logger } from '@/utils/logger/logger';

import { useQueryEntityVerification } from '../hooks/useQueryEntityVerification';

export interface SessionPollerProps {
  sessionId: string;
  /**
   * The analysis id the owning hook registered for the live generation.
   * `undefined` means there is no live registration (never registered, or
   * already dropped) — terminal results then require an observed in-flight
   * transition before they may fire.
   */
  expectedAnalysisId?: string;
  onTerminal: (
    sessionId: string,
    status: 'completed' | 'failed',
    analysisId: string
  ) => void;
}

/**
 * Invisible polling observer for a single session, mounted by
 * `AutoVerifyCrossSessionWatcher` for every entry in the in-flight set.
 * Mounting this component is enough to keep React Query's polling alive
 * because it subscribes to the shared query. Unmounted as soon as the session
 * leaves the in-flight set.
 */
export function SessionPoller({
  sessionId,
  expectedAnalysisId,
  onTerminal,
}: SessionPollerProps) {
  const { data: analysis } = useQueryEntityVerification(sessionId);
  const lastObservedRef = React.useRef<
    | {
        id: string;
        status: AnalysisStatus | undefined;
        expectedAnalysisId: string | undefined;
      }
    | undefined
  >(undefined);

  React.useEffect(() => {
    // Only auto-mode analyses participate in the cross-session flow — a manual
    // analysis resolving on a session still in the in-flight set must not
    // clear auto state or fire the auto toast (mirrors useAutoVerify's gate).
    if (analysis?.source !== 'auto') return;

    const prev = lastObservedRef.current;
    const cur = analysis.status;
    // Always recorded before any return so later polls compare against it.
    lastObservedRef.current = { id: analysis.id, status: cur, expectedAnalysisId };

    // Only terminal outcomes act; anything in flight just updates the
    // observation record above.
    if (cur !== 'completed' && cur !== 'failed') return;

    // When the owning hook registered this generation's analysis id (from the
    // start response or an adopted in-flight analysis), a terminal first
    // result is safe to accept without an observed in-flight transition — a
    // stale cached analysis cannot match the id.
    if (expectedAnalysisId && analysis.id === expectedAnalysisId) {
      onTerminal(sessionId, cur, analysis.id);
      return;
    }

    // The registered analysis was observed in-flight under the CURRENT
    // registration, and a different analysis has now terminated: the
    // registration was superseded. Fire even without an observed in-flight
    // status on the new analysis — the BE exposes only the latest analysis,
    // so a terminal result newer than the registered one can only be its
    // superseder (a stale cache from a previous generation is older than the
    // registered analysis and cannot displace it).
    const registeredWasSeenInFlight =
      expectedAnalysisId !== undefined &&
      prev?.id === expectedAnalysisId &&
      prev.expectedAnalysisId === expectedAnalysisId &&
      isAnalysisInFlight(prev.status);
    if (registeredWasSeenInFlight) {
      logger.warn('Auto-verify: registered analysis superseded', {
        sessionId,
        expectedAnalysisId,
        analysisId: analysis.id,
      });
      onTerminal(sessionId, cur, analysis.id);
      return;
    }

    // No registered id, or the previous observation predates the current
    // registration. Require an observed in-flight status on the SAME
    // analysis, observed under the SAME registration, before firing: the
    // first result may be a stale cached analysis from a previous run, and
    // an observation predating the current registration may belong to an
    // out-of-order older analysis whose completion must not clear the live
    // run's state.
    if (!prev || prev.id !== analysis.id) return;
    if (prev.expectedAnalysisId !== expectedAnalysisId) return;
    if (!isAnalysisInFlight(prev.status)) return;
    if (expectedAnalysisId) {
      logger.warn('Auto-verify: registered analysis superseded', {
        sessionId,
        expectedAnalysisId,
        analysisId: analysis.id,
      });
    }
    onTerminal(sessionId, cur, analysis.id);
  }, [
    analysis?.id,
    analysis?.source,
    analysis?.status,
    expectedAnalysisId,
    sessionId,
    onTerminal,
  ]);

  return null;
}
